import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { BellIcon, DocumentTextIcon, LightBulbIcon, HeartIcon, CogIcon, MapPinIcon, UserGroupIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import TeamManagement from '../components/TeamManagement';
import SourceMappingManager from '../components/SourceMappingManager';
import DataManagement from '../components/DataManagement';
import DisplaySettings from '../components/DisplaySettings';
import { useNotifications } from '../contexts/NotificationContext';

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { preferences, updatePreferences, isLoading: notificationsLoading } = useNotifications();
  const [localPreferences, setLocalPreferences] = useState(preferences);
  const [isSaving, setIsSaving] = useState(false);

  // Update local preferences when context preferences change
  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handlePreferenceChange = (key: keyof typeof preferences, value: boolean) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    try {
      await updatePreferences(localPreferences);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const notificationTypes = [
    {
      key: 'qbr_completed' as keyof typeof preferences,
      label: 'QBR Reports Completed',
      description: 'Get notified when quarterly business review reports are ready',
      icon: DocumentTextIcon,
      color: 'text-blue-500'
    },
    {
      key: 'insight_generated' as keyof typeof preferences,
      label: 'Insights Generated',
      description: 'Get notified when new AI insights are created',
      icon: LightBulbIcon,
      color: 'text-yellow-500'
    },
    {
      key: 'health_change' as keyof typeof preferences,
      label: 'Customer Health Changes',
      description: 'Get notified when customer health scores change significantly',
      icon: HeartIcon,
      color: 'text-red-500'
    },
    {
      key: 'integration_status' as keyof typeof preferences,
      label: 'Integration Status',
      description: 'Get notified when integrations connect, disconnect, or encounter errors',
      icon: CogIcon,
      color: 'text-green-500'
    },
    {
      key: 'mapping_required' as keyof typeof preferences,
      label: 'Mapping Required',
      description: 'Get notified when new tickets or issues need customer mapping',
      icon: MapPinIcon,
      color: 'text-orange-500'
    },
    {
      key: 'team_invitation' as keyof typeof preferences,
      label: 'Team Invitations',
      description: 'Get notified about team member invitations and status changes',
      icon: UserGroupIcon,
      color: 'text-purple-500'
    }
  ];
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Manage your account, data sources, and system preferences.</p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Notification Preferences Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BellIcon className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {notificationTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <div key={type.key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-start gap-3 flex-1">
                      <IconComponent className={`h-5 w-5 mt-0.5 ${type.color}`} />
                      <div className="flex-1">
                        <Label htmlFor={type.key} className="text-sm font-medium cursor-pointer">
                          {type.label}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {type.description}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id={type.key}
                      checked={localPreferences[type.key]}
                      onCheckedChange={(checked) => handlePreferenceChange(type.key, checked)}
                      disabled={notificationsLoading || isSaving}
                    />
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSavePreferences}
                disabled={isSaving || notificationsLoading}
                className="min-w-[100px]"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Management Section */}
        <Card>
          <CardHeader>
            <CardTitle>Data Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <SourceMappingManager onMappingUpdate={() => queryClient.invalidateQueries({ queryKey: ['integrations'] })} />
            <DataManagement />
          </CardContent>
        </Card>

        {/* Display Settings Section */}
        <DisplaySettings />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              Privacy & Legal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Review our privacy policy, data processing terms, and subprocessor list.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" asChild>
                <a href="/privacy?from=settings" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/dpa?from=settings" target="_blank" rel="noopener noreferrer">
                  Data Processing Agreement
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/subprocessors?from=settings" target="_blank" rel="noopener noreferrer">
                  Subprocessors
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/terms?from=settings" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">Account settings coming soon...</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Team Management</CardTitle>
          </CardHeader>
          <CardContent>
            <TeamManagement />
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default SettingsPage;
